// Multi-page white-label site: config types + pure helpers. A venue's website
// becomes a set of PAGES, each a stack of typed section BLOCKS, the reusable
// "design a whole site in the builder" system (Love Inn first). Rendering in
// components/site-pages.tsx; editing in the Website builder.

export type SiteBlock =
  | {
      type: "hero";
      imageUrl?: string;
      heading?: string;
      sub?: string;
      ctaLabel?: string;
      ctaHref?: string;
      cta2Label?: string;
      cta2Href?: string;
      /** Viewport-filling (home) vs banner height (inner pages). */
      full?: boolean;
      /** "outline": big bold hollow/stroke-only heading instead of the solid fill. Inner pages only. */
      headingStyle?: "solid" | "outline";
      /** Text + button alignment. Default "left" (existing behaviour). "center" mirrors the marketing landing page. */
      align?: "left" | "center";
      /**
       * Darkening / vignette overlay on the background image. Default true
       * (preserves the existing look). Set false when the image already has
       * high contrast and shouldn't be dimmed (Tyler @ Toka 2026-09-23).
       */
      dimOverlay?: boolean;
    }
  | { type: "text"; heading?: string; body: string }
  | {
      type: "imageText";
      imageUrl: string;
      heading?: string;
      body?: string;
      flip?: boolean;
      /** Optional inline button, renders directly under the body copy (Tyler @ Toka 2026-09-23). */
      buttonLabel?: string;
      buttonHref?: string;
    }
  // layout: "masonry" (default) mixes aspect ratios in a Pinterest-style
  // columns layout; "grid" forces every image to a uniform square in an
  // even grid, a neat collage instead.
  | { type: "gallery"; images: string[]; layout?: "masonry" | "grid" }
  | { type: "events"; heading?: string; limit?: number }
  // eventsCarousel: horizontal snap carousel of square posters. Two modes:
  //   1. Pull live Assembly events (venue with hasApp + tickets via us) —
  //      leave `items` unset.
  //   2. Static list, same shape as `agenda.items` — for venues whose tickets
  //      live off-platform (Love Inn on HeadFirst) but who still want the
  //      carousel look.
  | { type: "eventsCarousel"; heading?: string; limit?: number; items?: Array<{ date: string; title: string; sub?: string; imageUrl?: string; href: string }> }
  | {
      type: "agenda";
      heading?: string;
      /** Cap how many rows render (e.g. a trimmed "Coming up" on the home page). */
      limit?: number;
      items: Array<{ date: string; title: string; sub?: string; imageUrl?: string; href: string }>;
    }
  | {
      type: "faq";
      heading?: string;
      items: Array<{ q: string; a: string }>;
      imageUrl?: string;
      flip?: boolean;
      /**
       * Layout variant. Default (undefined/"default") = stacked heading + question
       * list, unchanged. "sidebar" = magazine-style two-column: heading on the
       * left, expandable questions on the right (Tyler @ Toka 2026-09-23
       * asked for the Oki Sauna reference style: "BOOKING & SESSIONS" label
       * on the left, questions on the right). Ignored when imageUrl is set,
       * the image-content split already owns the layout.
       */
      variant?: "default" | "sidebar";
    }
  | { type: "hours"; heading?: string; rows: Array<{ label: string; value: string }>; note?: string; imageUrl?: string; flip?: boolean }
  | { type: "contact"; heading?: string; body?: string; email?: string; phone?: string; address?: string; imageUrl?: string; flip?: boolean }
  | { type: "audio"; heading?: string; sub?: string; items: Array<{ title: string; href: string; imageUrl?: string; meta?: string }> }
  // Big clickable location/venue tiles (the "pick a site" splash, e.g. Next
  // Door's Shepherd's Bush / Stoke Newington / Online Shop landing).
  | {
      type: "tiles";
      heading?: string;
      items: Array<{ title: string; sub?: string; href: string; imageUrl?: string }>;
      /** Break out of the page's max-width container and span the full viewport (Tyler @ Toka 2026-09-23). */
      fullWidth?: boolean;
      /** Colour for tile title + subline (Tyler @ Toka: white/pick over dark imagery). Absent = existing muted/on-image colour. */
      textColor?: string;
    }
  // Full-viewport monochrome teaser / coming-soon splash (NDR's Red Room):
  // a kicker line, one huge wordmark, an optional sub-line + tagline, and an
  // optional mailing-list signup. Best paired with page.bare = true.
  | {
      type: "splash";
      backgroundColor?: string;
      foregroundColor?: string;
      kicker?: string;
      heading: string;
      sub?: string;
      tagline?: string;
      showMailingList?: boolean;
      mailingListPlaceholder?: string;
      /** Which named list on this venue to add signups to (default: the primary list). */
      listId?: string;
      // Font overrides. Either a Google Fonts family name (loaded via a link
      // tag on the fly), or a paired {name, url} to inject an @font-face for a
      // hosted custom font file (paid foundry fonts like BHH Bartle). URL wins
      // when both are set.
      headingFont?: string;
      bodyFont?: string;
      headingFontUrl?: string;
      bodyFontUrl?: string;
    }
  // imageUrl (optional): renders through the same image+content split used
  // by imageText/hours/faq, on the venue's normal dark background, instead
  // of today's centered white inverted panel. flip puts the image on the
  // right. Absent = unchanged white-panel behaviour.
  // listId (optional): named list on this venue to add signups to.
  // collectPhone / collectSmsConsent: opt in to capturing phone + PECR SMS
  // consent alongside email, so the SMS platform actually has audience.
  // Both default false → email-only form (existing behaviour).
  | {
      type: "subscribe";
      heading?: string;
      sub?: string;
      imageUrl?: string;
      flip?: boolean;
      listId?: string;
      collectPhone?: boolean;
      collectSmsConsent?: boolean;
    }
  | { type: "cta"; label: string; href: string }
  | {
      type: "appPromo";
      heading?: string;
      body?: string;
      /** Up to 3 app screenshots, shown as a fanned iPhone-mockup cluster. */
      screenshots: string[];
      ctaLabel?: string;
      ctaHref?: string;
    }
  // Pragmatic 2 / 3 -column grid block. Each cell is either an image or a
  // text card, arranged side-by-side on desktop and stacked on mobile. Toka
  // 2026-09-19: they want to build magazine-style layouts (image | text |
  // image) without waiting for the full free-form WYSIWYG grid. Stack
  // multiple `columns` blocks vertically for a multi-row grid page.
  | {
      type: "columns";
      cols: 2 | 3;
      cells: Array<
        | { kind: "text"; heading?: string; body?: string; align?: "left" | "center" }
        // Image cells: `title` renders above the image, `body` below.
        // Legacy `caption` was a single small footer line, kept for back-compat.
        // Tyler @ Toka 2026-09-23 wants both a header title and body text per tile.
        | { kind: "image"; imageUrl: string; caption?: string; title?: string; body?: string }
      >;
      /** Optional heading above the whole row. */
      heading?: string;
    }
  // Free-form canvas: absolute-positioned items (text / image) on a fixed-
  // height canvas. Positions are stored as PERCENTAGES so the layout scales
  // cleanly with viewport width. Deliberately MINIMAL for the first ship
  // (Toka 2026-09-19 asked for a Squarespace-style WYSIWYG; a full version
  // needs breakpoints, snap-to-grid, undo/redo, multi-select — weeks of
  // work). Scope for this MVP:
  //   - Fixed canvas height on desktop; author picks 300/500/700 px
  //   - Drag body to move, drag corner to resize (both %-based)
  //   - Two item kinds: text card + image
  //   - Mobile fallback: items stack vertically in author order (no
  //     per-breakpoint positions in v1). Editor calls this out.
  | {
      type: "freeform";
      /** Desktop canvas height in px. Ignored on mobile (items stack). */
      height: 300 | 500 | 700;
      /** Optional canvas background colour (hex). */
      bg?: string;
      items: Array<FreeformItem>;
    }
  // Dedicated festival-ticketing block: embed Festival tier / add-on /
  // accommodation purchase inline on any site page, filtered down to just
  // what this page is for. Fenton @ Madruga 2026-10-09: wants a
  // "/accommodation" page that ONLY sells accommodation (no main-event
  // tickets or extras), with the full checkout flow happening right there
  // (no redirect out to /festival). Repeatable in the website builder:
  // any venue with a FESTIVAL archetype event can add this block to any
  // page and filter down to the subset they want to sell on that URL.
  //
  // Renders by mounting <FestivalPage variant="tickets" filter={…}> inside
  // the page's content area, so the whole tier/add-on/accommodation card
  // UI + checkout flow is reused as-is (zero duplicated logic). The venue
  // template continues to serve the full festival page at /festival; this
  // block is a FOCUSED SUBSET for a dedicated page.
  | {
      type: "ticketing";
      /** Optional heading above the ticket cards. */
      heading?: string;
      /** Optional one-line description under the heading. */
      sub?: string;
      /**
       * Festival event id to pull inventory from. Omit to use the venue's
       * primary FESTIVAL event (most venues have exactly one, so omit by
       * default). Operators who run multiple events can pin a specific one.
       */
      eventId?: string;
      /**
       * Which product categories to show on this specific page. All three
       * default to true (show everything, same as /festival). Flip
       * off the ones you don't want on this URL, e.g. an /accommodation
       * page sets showTiers=false, showAccommodation=true.
       */
      showTiers?: boolean;
      showAddOns?: boolean;
      showAccommodation?: boolean;
    }
  // Signed safety / liability waiver (Toka 2026-10-11: extra party members
  // sign + submit before their session). Public form on the page: name,
  // email, typed-name signature, agreement checkbox. On submit the whole
  // waiverBody is snapshotted into the row so a future edit never rewrites
  // what was signed. Admin reads signatures via scripts/*.mjs for v1 (no
  // admin UI in this ship, kept small). Pair with page.bare = true for a
  // single-purpose waiver URL, or drop it into any multi-block page.
  | {
      type: "waiver";
      heading?: string;
      /**
       * The waiver copy itself. Rendered as plain paragraphs above the form.
       * This exact string is snapshotted onto every submitted row so later
       * edits to the block don't rewrite historical signatures.
       */
      waiverBody: string;
      /** Label next to the "I agree" checkbox. */
      agreementLabel?: string;
      /** Button text, e.g. "Sign and submit". */
      submitLabel?: string;
      /** Confirmation shown after a successful submit. */
      successMessage?: string;
    };

/**
 * One draggable item inside a `freeform` block. Position + size are % of the
 * canvas so the layout scales with the browser width; the renderer maps them
 * back to CSS `left/top/width/height` values on desktop, and stacks them
 * top-to-bottom on mobile (in author-added order).
 */
export type FreeformItem = {
  /** Stable id, used as the React key + the drag/select target. */
  id: string;
  /** Position as % of the canvas (0-100). */
  x: number;
  y: number;
  /** Size as % of the canvas (0-100). */
  w: number;
  h: number;
  /** Stacking order when items overlap. Higher = in front. */
  z?: number;
  /** Rotation in degrees. Absent / 0 = upright. Renderer applies as CSS transform. */
  rotation?: number;
} & (
  | {
      kind: "text";
      body: string;
      /** Font sizing tier; renderer maps to a Tailwind class. */
      size?: "sm" | "md" | "lg" | "xl";
      align?: "left" | "center" | "right";
      /** Optional bg colour for the text card (hex). */
      bg?: string;
    }
  | {
      kind: "image";
      imageUrl: string;
      alt?: string;
      /** Object-fit: "cover" (default, fills) or "contain" (letterbox). */
      fit?: "cover" | "contain";
      /**
       * Where the image sits inside its frame when fit=cover. 0-100% for
       * each axis; the standard CSS object-position mapping (0/0 = top-left,
       * 100/100 = bottom-right, default centres at 50/50). Lets an author
       * pan the crop without resizing the frame (Tyler @ Toka 2026-09-23:
       * "resizing the image box doesn't move the frame independently of
       * the image that is in the box"). Ignored when fit=contain.
       */
      objectPositionX?: number;
      objectPositionY?: number;
      /**
       * Zoom the image inside its frame (CSS transform: scale(N)). 1 = no
       * zoom (default); 2 = 200%. Combined with objectPositionX/Y this
       * gives a full crop editor in ~2 sliders. Skip the DOM transform when
       * absent or 1 so nothing changes for existing tags.
       */
      imageScale?: number;
    }
);

export type SitePage = {
  /** URL path segment; "" = the home page. */
  slug: string;
  title: string;
  /** Shown in the burger/nav menu. */
  inNav?: boolean;
  /** Shown as an always-visible primary button in the header. */
  primary?: boolean;
  /** Listed in the footer (privacy/terms). */
  footer?: boolean;
  /** Suppress the site nav + footer chrome (single-purpose splash pages). */
  bare?: boolean;
  /**
   * Draft vs live. Absent or true = live on the public site. False = draft:
   * hidden from nav/footer/direct-URL access for real visitors, still fully
   * rendered inside the admin preview iframe so operators can build a page
   * without it going live on save. Back-compat: pre-existing pages have no
   * `published` field, so they read as live (undefined !== false).
   */
  published?: boolean;
  blocks: SiteBlock[];
};

export type SitePagesConfig = {
  pages: SitePage[];
};

/**
 * Is this page visible to public visitors right now? Draft pages are hidden
 * from nav/footer/direct-URL access. Absent `published` = live for back-compat
 * with every page that existed before the draft flag was introduced.
 */
export function isPageLive(page: SitePage): boolean {
  return page.published !== false;
}

/**
 * Resolves which of three layouts the FAQ block should render.
 *   "image"    → an imageUrl is set; ImageContentSplit owns the two-column
 *                layout so any `variant` is ignored (documented in the type).
 *   "sidebar"  → variant explicitly opted in AND no image.
 *   "default"  → stacked heading + question list (unchanged pre-2026-09-25 look).
 * Pure so `tests/unit/site-pages-faq-layout.test.ts` can pin the mapping
 * without spinning up React.
 */
export function faqLayoutFor(
  variant: "default" | "sidebar" | undefined,
  hasImage: boolean,
): "image" | "sidebar" | "default" {
  if (hasImage) return "image";
  return variant === "sidebar" ? "sidebar" : "default";
}

/**
 * Slug aliases that should resolve to the HOME page. The site-page editor
 * historically let operators type whatever URL path they wanted, and
 * "home" / "index" / "/" are all natural things to type for a page labelled
 * "Home". Treat them all as the home slug ("") so the admin flip to Full
 * site renders the operator's intended home instead of silently falling
 * through to the default venue template (Toka, 2026-10-01).
 */
const HOME_SLUG_ALIASES = new Set(["", "home", "index"]);

function isHomeSlug(slug: string | undefined): boolean {
  return HOME_SLUG_ALIASES.has((slug ?? "").toLowerCase());
}

/**
 * The page for a pathname ("/" -> slug "", "/whats-on" -> "whats-on"). By
 * default filters draft pages out (they behave as if they don't exist for
 * public visitors); pass `{ includeDrafts: true }` from the admin preview
 * iframe so operators can navigate to a page they haven't published yet.
 *
 * HOME resolution: "/" matches any page whose slug is in HOME_SLUG_ALIASES
 * ("", "home", "index"). Non-home paths match on exact slug.
 *
 * EMPTY-PAGE GUARD (Tyler @ Toka 2026-10-01 launch day): a sitePage with
 * zero blocks renders as an empty shell (nav + footer, nothing in between)
 * and silently hijacks any built-in route sharing the slug (e.g. /book
 * which has its own BookSessionsPage renderer). Public visitors see a
 * blank page; the real content is one layer behind. Treat an empty
 * published page as "not configured yet" and return null so the router
 * falls through to whatever handles the path next. The admin preview
 * (includeDrafts) still returns the empty page so operators can see +
 * build it.
 */
function pageHasRenderableContent(page: SitePage): boolean {
  return Array.isArray(page.blocks) && page.blocks.length > 0;
}

export function pageForPath(
  cfg: SitePagesConfig | undefined | null,
  pathname: string,
  opts?: { includeDrafts?: boolean },
): SitePage | null {
  if (!cfg?.pages?.length) return null;
  const slug = pathname.replace(/^\/+|\/+$/g, "");
  const match = isHomeSlug(slug)
    ? cfg.pages.find((p) => isHomeSlug(p.slug)) ?? null
    : cfg.pages.find((p) => (p.slug ?? "") === slug) ?? null;
  if (!match) return null;
  if (opts?.includeDrafts) return match;
  if (!isPageLive(match)) return null;
  // Published but EMPTY = fall through to the built-in route for this path.
  if (!pageHasRenderableContent(match)) return null;
  return match;
}

/**
 * Normalise a user-typed URL path to the canonical slug stored on
 * `SitePage.slug`. Strips leading/trailing slashes, lowercases, and
 * collapses any home alias ("home", "index", "/") to "" so the router's
 * "/" matching stays simple. Called from the admin page editor on save
 * so a venue created today gets the clean shape Love Inn has (Toka
 * 2026-10-01: new venues were saving `slug: "home"`, which the router
 * never matched against "/", so the whole Full-site flip silently fell
 * through to the default template).
 */
export function normaliseSitePageSlug(input: string): string {
  const trimmed = input.trim().replace(/^\/+|\/+$/g, "").toLowerCase();
  return isHomeSlug(trimmed) ? "" : trimmed;
}

/**
 * Convert a human page title (e.g. "Gallery", "Venue hire", "What's on") into
 * the canonical URL slug stored on `SitePage.slug`. Lowercase, non-alphanumeric
 * collapsed to dashes, trimmed, length-capped at 40 chars (longer paths get
 * ugly), then passed through `normaliseSitePageSlug` so a title of "Home" /
 * "Index" lands at slug="" (the canonical home key the router matches "/" on).
 *
 * Exported for reuse: the site-pages editor's `+ Add page` flow + unit tests.
 * Shipped as part of the 2026-10-09 "Gallery page won't appear" fix (Sam @
 * Madruga): the editor used to special-case the first page to slug="" and
 * then disable the URL path input, trapping operators in an invisible-page
 * state. Now every new page derives its slug from the title, and the input
 * is always editable so operators can retitle "Home" if they want to make
 * one explicitly.
 */
export function slugifyTitle(title: string): string {
  const base = title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40);
  return normaliseSitePageSlug(base);
}

/**
 * Build the default `SitePage` object for a freshly-added page in the admin
 * editor. Keeps the slug-derivation + sensible defaults (inNav on, published
 * off = draft) in a pure function the editor + tests both call.
 *
 * Fallback for empty-slug-after-slugify (e.g. operator typed "---" or an
 * emoji-only title): use `page-N` where N is the next 1-based index, so the
 * page lands at a predictable URL instead of silently becoming a second
 * home page. "Home" / "Index" (which legitimately slugify to "") are still
 * allowed through, so operators can create a home page by naming it that.
 */
export function createNewSitePage(
  title: string,
  existingCount: number,
): SitePage {
  const slug = slugifyTitle(title);
  // "Home" / "Index" naturally slugify to "", which is the home slug (that's
  // the intended way to make a home page). Any OTHER input that happens to
  // slugify to "" (symbols-only titles) would silently become home,  bad UX,
  // fall back to a deterministic page-N slug the operator can edit.
  const isIntentionalHome = /^(home|index|\/)$/i.test(title.trim());
  const safeSlug = slug === "" && !isIntentionalHome ? `page-${existingCount + 1}` : slug;
  return {
    slug: safeSlug,
    title,
    inNav: true,
    published: false,
    blocks: [],
  };
}

export const BLOCK_LABELS: Record<SiteBlock["type"], string> = {
  hero: "Hero (image + heading + buttons)",
  text: "Text",
  imageText: "Image + text",
  gallery: "Photo gallery",
  events: "What's on (live events)",
  eventsCarousel: "Upcoming events (horizontal carousel, from the app)",
  agenda: "Agenda (grouped by month, static list)",
  faq: "FAQ / accordion",
  hours: "Opening hours",
  contact: "Contact details",
  audio: "Audio archive (links)",
  tiles: "Location tiles (big clickable cards)",
  splash: "Splash / coming soon (full-viewport teaser)",
  subscribe: "Mailing-list signup",
  cta: "Button",
  appPromo: "Get the app (phone mockups + store link)",
  columns: "Columns (side-by-side text + image cells)",
  freeform: "Free-form canvas (drag + resize text/image, desktop-only positions)",
  ticketing: "Festival ticketing (filtered: tiers / add-ons / accommodation)",
  waiver: "Safety waiver (name + email + typed signature)",
};

/** A sensible empty block of each type (the builder's "Add block"). */
export function emptyBlock(type: SiteBlock["type"]): SiteBlock {
  switch (type) {
    case "hero":
      return { type, heading: "", sub: "" };
    case "text":
      return { type, heading: "", body: "" };
    case "imageText":
      return { type, imageUrl: "", heading: "", body: "" };
    case "gallery":
      return { type, images: [] };
    case "events":
      return { type, heading: "What's on", limit: 12 };
    case "eventsCarousel":
      return { type, heading: "Upcoming events", limit: 12 };
    case "agenda":
      return { type, heading: "Coming up", items: [] };
    case "faq":
      return { type, items: [] };
    case "hours":
      return { type, heading: "Opening hours", rows: [] };
    case "contact":
      return { type, heading: "Contact" };
    case "audio":
      return { type, heading: "Audio archive", items: [] };
    case "tiles":
      return { type, items: [] };
    case "splash":
      return {
        type,
        backgroundColor: "#B80011",
        foregroundColor: "#422126",
        heading: "Coming soon",
        tagline: "coming soon...",
        showMailingList: true,
      };
    case "subscribe":
      return { type, heading: "Subscribe", sub: "" };
    case "cta":
      return { type, label: "", href: "" };
    case "appPromo":
      return { type, heading: "Get the app", screenshots: [], ctaLabel: "Download now" };
    case "columns":
      // Default: 2 columns, one image + one text cell. Author picks kinds
      // per cell in the editor. Empty imageUrl reads as "not set yet" and
      // the renderer shows a placeholder so the layout is still visible.
      return {
        type,
        cols: 2,
        cells: [
          { kind: "image", imageUrl: "" },
          { kind: "text", heading: "", body: "" },
        ],
      };
    case "freeform":
      // Default: a medium canvas with one placeholder text card top-left so
      // the block isn't empty on first add. Author clicks "+ Add text" /
      // "+ Add image" in the editor for more.
      return {
        type,
        height: 500,
        items: [
          {
            id: "seed-" + Math.random().toString(36).slice(2, 8),
            kind: "text",
            x: 8,
            y: 8,
            w: 40,
            h: 20,
            body: "Drag me. Drag the corner to resize. Drag the handle up top to rotate.",
            size: "md",
            align: "left",
          },
        ],
      };
    case "ticketing":
      // Default: show everything (same as the venue's /festival page). The
      // operator ticks off whichever categories don't belong on this URL,
      // e.g. an /accommodation page unchecks showTiers + showAddOns.
      return {
        type,
        heading: "Tickets",
        showTiers: true,
        showAddOns: true,
        showAccommodation: true,
      };
    case "waiver":
      // Sensible starter copy the operator will replace with their actual
      // waiver text. Not legally reviewed, deliberately generic.
      return {
        type,
        heading: "Safety waiver",
        waiverBody:
          "I confirm I am over 18 and understand that use of the facilities is at my own risk. I will follow all safety guidance provided by staff. I understand that failure to follow these guidelines may result in injury.",
        agreementLabel: "I have read and agree to the safety waiver above.",
        submitLabel: "Sign and submit",
        successMessage: "Thanks, your signature has been recorded.",
      };
  }
}

/**
 * Filter applied by a `ticketing` SiteBlock when it mounts <FestivalPage
 * variant="tickets">. Flipping a flag to false hides that category entirely
 * (not just empties it), so e.g. a page with only showAccommodation=true
 * won't render Step 1 "Tickets" or Step 2 "Extras" sections at all.
 *
 * Exported so FestivalPage can type its new `filter` prop against the same
 * shape the SiteBlock stores.
 */
export type FestivalCategoryFilter = {
  showTiers?: boolean;
  showAddOns?: boolean;
  showAccommodation?: boolean;
};

/**
 * Resolve the three category flags from the ticketing block config to the
 * final boolean values applied by the renderer. Undefined = true (back-
 * compat with any ticketing block that doesn't carry explicit flags).
 * Pure so site-pages-ticketing-filter.test.ts can pin the mapping.
 */
export function resolveFestivalCategoryFilter(
  block: { showTiers?: boolean; showAddOns?: boolean; showAccommodation?: boolean },
): Required<FestivalCategoryFilter> {
  return {
    showTiers: block.showTiers !== false,
    showAddOns: block.showAddOns !== false,
    showAccommodation: block.showAccommodation !== false,
  };
}
